import { bearerSubprotocols } from './bearer-subprotocol'

/*
 * DLITE-5094: client for commentary-ws's multiplexed thread-event socket.
 *
 * Every wire literal of the server contract lives in this block, so a change
 * to the (still open) commentary-ws MR !107 contract is a one-place edit.
 * Contract as of !107 @ ef3ac827 (`ThreadsV3Socket.kt`, `ThreadEventNotifier.kt`):
 *  - client → server: `{type:'subscribe'|'unsubscribe', accountIds}`; the
 *    server takes at most 200 ids per frame and holds at most 200 per
 *    connection, silently dropping the excess.
 *  - server → client: `{type:'subscribed', accountIds}` (the sorted CURRENT
 *    set) answers each subscribe/unsubscribe; a requested id missing from it
 *    was denied. Any other client frame is answered `{type:'ping', echo}`.
 *  - events: `{type:'thread', thread}` and `{type:'invalidate', accountId}`.
 *  - the server sends no unsolicited heartbeat, so the client originates one.
 *  - close 1008: the principal lacks `get-threads`; reconnecting cannot help.
 */
export const THREADS_WS_PATH = '/plugin/commentary-ws/external/v3/threads/ws'
export const WS_SUBSCRIBE = 'subscribe'
export const WS_UNSUBSCRIBE = 'unsubscribe'
export const WS_SUBSCRIBED = 'subscribed'
export const WS_PING = 'ping'
export const WS_THREAD_EVENT = 'thread'
export const WS_INVALIDATE_EVENT = 'invalidate'
export const WS_POLICY_VIOLATION_CLOSE = 1008
/** Close code used when the client itself drops a connection (watchdog). */
export const WS_CLIENT_RECONNECT_CLOSE = 4000

// Timing mirrors commentary-ui's threads-ws-manager.ts, which runs against the
// same server today.
export const HEARTBEAT_INTERVAL_MS = 25_000
export const WATCHDOG_TIMEOUT_MS = 60_000
export const RECONNECT_BASE_MS = 1_000
export const RECONNECT_MAX_MS = 30_000
export const MAX_RECONNECT_ATTEMPTS = 12
/** A connection must stay open this long before its reconnect backoff resets.
 *  Resetting on open would let a server or proxy that accepts and then drops
 *  the connection loop at RECONNECT_BASE_MS forever (each round reloading
 *  every displayed account); a `subscribed` reply is not enough either, since
 *  a flapping server can answer before it drops. */
export const STABLE_CONNECTION_MS = 10_000
/** Delays before re-subscribing ids the server denied, per attempt (each
 *  multiplied by a jitter in [0.5, 1]). A denial may be transient (a helios
 *  membership hiccup); after the last attempt the ids wait for the next
 *  reconnect, which re-subscribes the whole assigned set anyway. */
export const DENIAL_RETRY_DELAYS_MS = [5_000, 15_000, 45_000, 120_000, 300_000]
/** Rotate the handshake token this long before its JWT `exp`… */
export const TOKEN_ROTATE_LEAD_MS = 120_000
/** …but never sooner than this after the connection opened. */
export const TOKEN_ROTATE_MIN_MS = 30_000
/** Rotation period when the token's `exp` cannot be read. */
export const TOKEN_ROTATE_FALLBACK_MS = 900_000
/** A denial against a token older than this rotates instead of re-sending. */
export const TOKEN_SUSPECT_AGE_MS = 60_000
/** A rotation candidate that has not answered its subscribe by then is
 *  dropped (the old socket stays in use). */
export const ROTATION_REPLY_TIMEOUT_MS = 60_000
/** Failed rotations retried this many times (30 s, 60 s, 120 s) before the
 *  connection waits for its next reconnect to renew the token. */
export const ROTATION_MAX_RETRIES = 3

const WS_OPEN = 1

/** The JWT payload's `exp` in epoch ms, or null when it cannot be read. */
export function decodeJwtExpMs(token: string): number | null {
  const part = token.split('.')[1]
  if (!part) return null
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    const exp = JSON.parse(atob(padded))?.exp
    return typeof exp === 'number' && isFinite(exp) ? exp * 1000 : null
  } catch {
    return null
  }
}

/** Jittered delay for retry `attempt` from `DENIAL_RETRY_DELAYS_MS`. */
export function denialRetryDelay(attempt: number): number {
  const base =
    DENIAL_RETRY_DELAYS_MS[Math.min(attempt, DENIAL_RETRY_DELAYS_MS.length - 1)]
  return Math.floor(base * (0.5 + 0.5 * Math.random()))
}

export interface ThreadEventSocketCallbacks {
  /** A thread changed in these accounts (unfiltered — may include ids the
   *  caller never asked about, e.g. a thread's linked accounts). */
  onEvent(accountIds: string[]): void
  /** The server confirmed these requested accounts as subscribed. For a
   *  reconnect reply this is called after onReconnected for the same ids. */
  onAdmitted(accountIds: string[]): void
  /** The server refused to subscribe these requested accounts. */
  onDenied(accountIds: string[]): void
  /** A connection re-opened after a drop and its open-time subscribe
   *  admitted these accounts; anything may have changed while it was down.
   *  Not called for a connection's first open, unless attempts failed before
   *  it (the accounts were then shown with no socket covering them). */
  onReconnected(accountIds: string[]): void
}

export interface ThreadEventSocketPoolDeps {
  url: string
  /** `forceRefresh` bypasses the auth cache (used for token rotation). */
  getToken: (forceRefresh?: boolean) => Promise<string>
  createSocket: (url: string, protocols: string[]) => WebSocket
  /** Subscription cap per connection — and per frame. */
  maxPerConnection: number
  callbacks: ThreadEventSocketCallbacks
  /** Where to listen for `online`/`focus`/`visibilitychange` once reconnect
   *  attempts are exhausted. Defaults to the global window when present. */
  wakeTarget?: Pick<Window, 'addEventListener' | 'removeEventListener'> | null
  /** Wall clock, for token age and expiry. Defaults to Date.now. */
  now?: () => number
}

const WAKE_EVENTS = ['online', 'focus', 'visibilitychange']

interface PendingRequest {
  kind: typeof WS_SUBSCRIBE | typeof WS_UNSUBSCRIBE
  ids: string[]
  /** The subscribe a connection sends on open for its whole assigned set. */
  reconnect: boolean
}

type Timer = ReturnType<typeof setTimeout>

class ThreadEventConnection {
  /** Accounts placed on this connection; never more than maxPerConnection.
   *  Denied accounts stay assigned so release and reconnect treat every
   *  account the same way, and the server can never truncate. */
  readonly assigned = new Set<string>()
  /** Last `subscribed` reply: the server's authoritative current set. */
  serverSet = new Set<string>()

  private socket: WebSocket | null = null
  private connecting = false
  private disposed = false
  private policyClosed = false
  private attempts = 0
  /** Set on the first successful open; a later open is a reconnect. */
  private hasOpened = false
  /** Set when an attempt failed before the first open (token fetch, socket
   *  construction, a close before open, retry exhaustion): the accounts were
   *  shown — and their counts loaded over HTTP — with no socket covering
   *  them, so the first open must catch up like a reconnect. */
  private missedBeforeFirstOpen = false
  exhausted = false
  private pending: PendingRequest[] = []
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private watchdogTimer: Timer | null = null
  private reconnectTimer: Timer | null = null
  private stableTimer: Timer | null = null
  /** Denied ids still assigned → denial retries already sent for them. */
  private readonly deniedRetry = new Map<string, number>()
  private denialTimer: Timer | null = null
  private rotationTimer: Timer | null = null
  private rotating = false
  /** The not-yet-swapped-in socket of a rotation in progress. */
  private rotatingSocket: WebSocket | null = null
  /** When the current socket's token was fetched, and when it expires. */
  private tokenAt = 0
  private tokenExpMs: number | null = null
  /** The first denial retry against an old token rotates instead. */
  private rotatedForDenial = false
  /** Failed rotations since the last success; bounds rotation retries. */
  private rotationFailures = 0
  private rotationDeadline: Timer | null = null

  constructor(
    private readonly deps: ThreadEventSocketPoolDeps,
    private readonly onExhausted: () => void,
  ) {}

  get capacity(): number {
    return this.deps.maxPerConnection - this.assigned.size
  }

  add(ids: string[]): void {
    ids.forEach(id => this.assigned.add(id))
    if (this.isOpen()) this.sendRequest(WS_SUBSCRIBE, ids, false)
    else this.ensureConnecting()
  }

  remove(ids: string[]): void {
    ids.forEach(id => {
      this.assigned.delete(id)
      this.deniedRetry.delete(id)
    })
    if (this.deniedRetry.size === 0) this.clearDenialTimer()
    if (this.isOpen()) this.sendRequest(WS_UNSUBSCRIBE, ids, false)
  }

  /** Called on online/focus/visibilitychange after attempts ran out. */
  wake(): void {
    if (!this.exhausted || this.disposed) return
    this.exhausted = false
    this.attempts = 0
    this.ensureConnecting()
  }

  dispose(): void {
    this.disposed = true
    this.clearTimers()
    this.dropSocket(1000)
  }

  private isOpen(): boolean {
    return !!this.socket && this.socket.readyState === WS_OPEN
  }

  /** True when this connection is open and the server's latest reply
   *  confirms `id` as subscribed, i.e. a change to it will arrive here. */
  isLive(id: string): boolean {
    return this.isOpen() && this.serverSet.has(id)
  }

  private ensureConnecting(): void {
    if (
      this.disposed ||
      this.policyClosed ||
      this.exhausted ||
      this.socket ||
      this.connecting ||
      this.reconnectTimer
    ) {
      return
    }
    void this.connect()
  }

  private async connect(): Promise<void> {
    this.connecting = true
    let token: string
    try {
      // Fresh per connect: getToken renews a nearly-expired token.
      token = await this.deps.getToken()
    } catch {
      this.connecting = false
      this.scheduleReconnect()
      return
    }
    this.connecting = false
    if (this.disposed) return
    let socket: WebSocket
    try {
      socket = this.deps.createSocket(this.deps.url, bearerSubprotocols(token))
    } catch {
      this.scheduleReconnect()
      return
    }
    this.socket = socket
    this.pending = []
    this.adoptToken(token)
    this.rotatedForDenial = false
    this.rotationFailures = 0
    this.attachHandlers(socket)
  }

  private attachHandlers(socket: WebSocket): void {
    socket.onopen = () => this.handleOpen(socket)
    socket.onmessage = (ev: MessageEvent) => this.handleMessage(socket, ev)
    socket.onclose = (ev: CloseEvent) => this.handleClose(socket, ev)
    socket.onerror = () => undefined // onclose follows; nothing to log
  }

  private now(): number {
    return this.deps.now ? this.deps.now() : Date.now()
  }

  private adoptToken(token: string): void {
    this.tokenAt = this.now()
    this.tokenExpMs = decodeJwtExpMs(token)
  }

  /** Rotate shortly before the handshake token expires (or on a fixed
   *  period when its expiry cannot be read). */
  private scheduleRotation(): void {
    if (this.rotationTimer) clearTimeout(this.rotationTimer)
    const delay =
      this.tokenExpMs === null
        ? TOKEN_ROTATE_FALLBACK_MS
        : Math.max(
            this.tokenExpMs - TOKEN_ROTATE_LEAD_MS - this.now(),
            TOKEN_ROTATE_MIN_MS,
          )
    this.rotationTimer = setTimeout(() => {
      this.rotationTimer = null
      void this.rotate()
    }, delay)
  }

  /**
   * Make-before-break token rotation: open a second socket with a freshly
   * renewed token, subscribe it to the whole assigned set, forward any
   * membership change made meanwhile, and only once it has confirmed the
   * whole current set swap it in and close the old one with 1000. The old
   * socket stays live until the swap, so nothing is missed and onReconnected
   * is not called; duplicate events during the overlap are coalesced by the
   * caller. If the new socket fails before replying, the old one stays.
   */
  private async rotate(): Promise<void> {
    if (this.rotating || this.disposed || !this.isOpen()) return
    this.rotating = true
    let token: string
    try {
      // Bypass the auth cache, which would hand back the near-expiry token.
      token = await this.deps.getToken(true)
    } catch {
      this.rotationFailed()
      return
    }
    if (this.disposed || !this.isOpen()) {
      this.rotating = false
      return
    }
    let next: WebSocket
    try {
      next = this.deps.createSocket(this.deps.url, bearerSubprotocols(token))
    } catch {
      this.rotationFailed()
      return
    }
    // What the candidate has been asked to hold, and how many `subscribed`
    // replies it still owes. Membership changes made during the handoff go to
    // the old socket (which keeps serving them) and are forwarded to the
    // candidate as deltas; it is swapped in only once it has confirmed the
    // whole current assigned set, so no account is ever left unserved.
    let target = new Set(this.assigned)
    let owed = 0
    const sendToNext = (kind: PendingRequest['kind'], accountIds: string[]) => {
      for (let i = 0; i < accountIds.length; i += this.deps.maxPerConnection) {
        owed += 1
        try {
          next.send(
            JSON.stringify({
              type: kind,
              accountIds: accountIds.slice(i, i + this.deps.maxPerConnection),
            }),
          )
        } catch {
          // onclose follows
        }
      }
    }
    const abandon = () => {
      next.onopen = null
      next.onmessage = null
      next.onclose = null
      next.onerror = null
      if (this.rotatingSocket === next) {
        this.rotatingSocket = null
        this.rotating = false
        this.clearRotationDeadline()
      }
    }
    this.rotatingSocket = next
    // A candidate that never answers must not wedge rotation: give up on it
    // (keeping the old socket) after the same silence the watchdog allows.
    this.rotationDeadline = setTimeout(() => {
      this.rotationDeadline = null
      if (this.rotatingSocket !== next) return
      abandon()
      try {
        next.close(1000)
      } catch {
        // already closed
      }
      this.rotationFailed()
    }, ROTATION_REPLY_TIMEOUT_MS)
    next.onerror = () => undefined
    next.onclose = () => {
      const current = this.rotatingSocket === next
      abandon()
      // Keep the old socket; its eventual close takes the normal backoff.
      if (current) this.rotationFailed()
    }
    next.onopen = () => {
      if (this.rotatingSocket !== next) return
      // One frame for the whole set (≤ the cap); an empty set is still sent
      // so the candidate answers and the swap can complete.
      owed += 1
      try {
        next.send(
          JSON.stringify({ type: WS_SUBSCRIBE, accountIds: [...target] }),
        )
      } catch {
        // onclose follows
      }
    }
    next.onmessage = (ev: MessageEvent) => {
      if (this.rotatingSocket !== next) return
      let msg: Record<string, unknown>
      try {
        msg = JSON.parse(String(ev.data))
      } catch {
        return
      }
      // Until the swap the old socket carries events; only the reply counts.
      if (msg?.['type'] !== WS_SUBSCRIBED) return
      owed = Math.max(0, owed - 1)
      if (owed > 0) return
      // Catch the candidate up with what changed since it was last asked.
      // Removals first: the server silently drops subscriptions over its
      // per-connection cap, and the candidate may hold a full set.
      const removed = [...target].filter(id => !this.assigned.has(id))
      const added = [...this.assigned].filter(id => !target.has(id))
      if (removed.length > 0 || added.length > 0) {
        target = new Set(this.assigned)
        if (removed.length > 0) sendToNext(WS_UNSUBSCRIBE, removed)
        if (added.length > 0) sendToNext(WS_SUBSCRIBE, added)
        return
      }
      // The candidate now confirms the whole current set: swap it in.
      abandon()
      this.swapIn(next, token, msg['accountIds'])
    }
  }

  private swapIn(next: WebSocket, token: string, reply: unknown): void {
    if (this.disposed || !this.socket) {
      try {
        next.close(1000)
      } catch {
        // already closed
      }
      return
    }
    const old = this.socket
    old.onopen = null
    old.onmessage = null
    old.onclose = null
    old.onerror = null
    try {
      old.close(1000)
    } catch {
      // already closed
    }
    this.socket = next
    this.attachHandlers(next)
    this.adoptToken(token)
    this.rotationFailures = 0
    // The reply answers the candidate's last frame and carries its full
    // current set, which by now covers the whole assigned set.
    this.pending = [
      { kind: WS_SUBSCRIBE, ids: [...this.assigned], reconnect: false },
    ]
    this.resetWatchdog()
    this.scheduleRotation()
    // Treated like any subscribe reply: denials mark and retry, admissions
    // of marked ids recover.
    this.handleSubscribed(reply)
  }

  private handleOpen(socket: WebSocket): void {
    if (socket !== this.socket) return
    this.scheduleRotation()
    // Reset the backoff only once the connection has proven stable; a
    // connection that opens and then drops keeps climbing the backoff and
    // eventually exhausts MAX_RECONNECT_ATTEMPTS.
    this.stableTimer = setTimeout(() => {
      this.stableTimer = null
      this.attempts = 0
    }, STABLE_CONNECTION_MS)
    this.resetWatchdog()
    this.heartbeatTimer = setInterval(
      () => this.send({ type: WS_PING }),
      HEARTBEAT_INTERVAL_MS,
    )
    // Only a re-open after this connection had already opened once is a
    // reconnect: events may have been missed while it was down, so its
    // admitted accounts are reported through onReconnected and reloaded. The
    // first open is a plain subscribe — the grids have just loaded these
    // accounts, and reloading them again would fetch everything twice —
    // unless earlier attempts failed first: then the HTTP loads predate an
    // interval no socket covered, and the first open catches up too.
    const reconnect = this.hasOpened || this.missedBeforeFirstOpen
    this.hasOpened = true
    this.missedBeforeFirstOpen = false
    if (this.assigned.size > 0) {
      this.sendRequest(WS_SUBSCRIBE, [...this.assigned], reconnect)
    }
  }

  private handleMessage(socket: WebSocket, ev: MessageEvent): void {
    if (socket !== this.socket) return
    this.resetWatchdog()
    let msg: Record<string, unknown>
    try {
      msg = JSON.parse(String(ev.data))
    } catch {
      return
    }
    if (!msg || typeof msg !== 'object') return
    switch (msg['type']) {
      case WS_SUBSCRIBED:
        this.handleSubscribed(msg['accountIds'])
        return
      case WS_INVALIDATE_EVENT: {
        const id = msg['accountId']
        if (id !== undefined && id !== null && id !== '') {
          this.deps.callbacks.onEvent([String(id)])
        }
        return
      }
      case WS_THREAD_EVENT: {
        const ids = threadAccountIds(msg['thread'])
        if (ids.length > 0) this.deps.callbacks.onEvent(ids)
        return
      }
      default:
        // `ping` replies and anything unknown only feed the watchdog.
        return
    }
  }

  private handleSubscribed(raw: unknown): void {
    const current = new Set(
      (Array.isArray(raw) ? raw : []).map(id => String(id)),
    )
    // Every reply carries the full current set, so the latest one is
    // authoritative for what is subscribed even if correlation slips.
    this.serverSet = current
    // Replies are correlated with requests in FIFO order (the server handles
    // one connection's frames serially).
    const request = this.pending.shift()
    if (!request || request.kind !== WS_SUBSCRIBE) return
    const admitted: string[] = []
    const denied: string[] = []
    for (const id of request.ids) {
      // An id released while the request was in flight is neither.
      if (!this.assigned.has(id)) continue
      if (current.has(id)) admitted.push(id)
      else denied.push(id)
    }
    const { callbacks } = this.deps
    admitted.forEach(id => this.deniedRetry.delete(id))
    denied.forEach(id => {
      if (!this.deniedRetry.has(id)) this.deniedRetry.set(id, 0)
    })
    if (this.deniedRetry.size === 0) this.clearDenialTimer()
    if (denied.length > 0) {
      callbacks.onDenied(denied)
      this.scheduleDenialRetry()
    }
    // A reconnect is reported before the admissions of the same reply, so a
    // listener can tell an admission already covered by the reconnect reload
    // (and not load it a second time).
    if (request.reconnect && admitted.length > 0) {
      callbacks.onReconnected(admitted)
    }
    if (admitted.length > 0) callbacks.onAdmitted(admitted)
  }

  /** One batched timer per connection re-subscribes every denied id that has
   *  retries left; a reply that admits an id ends its retries. */
  private scheduleDenialRetry(): void {
    if (this.denialTimer || this.disposed) return
    const attempts = [...this.deniedRetry.values()].filter(
      n => n < DENIAL_RETRY_DELAYS_MS.length,
    )
    if (attempts.length === 0) return
    this.denialTimer = setTimeout(() => {
      this.denialTimer = null
      this.retryDenied()
    }, denialRetryDelay(Math.min(...attempts)))
  }

  private retryDenied(): void {
    if (!this.isOpen()) return // the open-time subscribe retries them all
    const ids: string[] = []
    this.deniedRetry.forEach((n, id) => {
      if (n >= DENIAL_RETRY_DELAYS_MS.length || !this.assigned.has(id)) return
      this.deniedRetry.set(id, n + 1)
      ids.push(id)
    })
    if (ids.length === 0) return
    // A denial against a token that is no longer fresh may be the token
    // itself: rotate (which re-subscribes the whole set with a renewed
    // token) instead of re-sending with the same token.
    if (
      !this.rotatedForDenial &&
      this.now() - this.tokenAt > TOKEN_SUSPECT_AGE_MS
    ) {
      this.rotatedForDenial = true
      void this.rotate()
      return
    }
    this.sendRequest(WS_SUBSCRIBE, ids, false)
  }

  private rotationFailed(): void {
    this.rotating = false
    this.clearRotationDeadline()
    // The old socket stays in use. Try again a bounded number of times with
    // a growing delay; after that the next reconnect gets a fresh token.
    if (
      !this.disposed &&
      this.isOpen() &&
      this.rotationFailures < ROTATION_MAX_RETRIES
    ) {
      const delay = TOKEN_ROTATE_MIN_MS * Math.pow(2, this.rotationFailures)
      this.rotationFailures += 1
      if (this.rotationTimer) clearTimeout(this.rotationTimer)
      this.rotationTimer = setTimeout(() => {
        this.rotationTimer = null
        void this.rotate()
      }, delay)
    }
    // A rotation standing in for a denial retry must not end the retries.
    this.scheduleDenialRetry()
  }

  private clearRotationDeadline(): void {
    if (this.rotationDeadline) clearTimeout(this.rotationDeadline)
    this.rotationDeadline = null
  }

  private abortRotation(): void {
    const next = this.rotatingSocket
    this.rotatingSocket = null
    this.rotating = false
    this.clearRotationDeadline()
    if (!next) return
    next.onopen = null
    next.onmessage = null
    next.onclose = null
    next.onerror = null
    try {
      next.close(1000)
    } catch {
      // already closed
    }
  }

  private clearDenialTimer(): void {
    if (this.denialTimer) clearTimeout(this.denialTimer)
    this.denialTimer = null
  }

  private handleClose(socket: WebSocket, ev: CloseEvent): void {
    if (socket !== this.socket) return
    this.clearTimers()
    this.socket = null
    this.pending = []
    // Nothing is subscribed on a closed connection until the re-open reply.
    this.serverSet = new Set()
    if (this.disposed) return
    if (ev?.code === WS_POLICY_VIOLATION_CLOSE) {
      // Not authorised for thread events at all; retrying cannot help.
      this.policyClosed = true
      return
    }
    this.scheduleReconnect()
  }

  private scheduleReconnect(): void {
    if (this.disposed || this.policyClosed || this.reconnectTimer) return
    // A failure before the first open leaves the shown accounts uncovered.
    if (!this.hasOpened) this.missedBeforeFirstOpen = true
    if (this.attempts >= MAX_RECONNECT_ATTEMPTS) {
      this.exhausted = true
      this.onExhausted()
      return
    }
    // Full jitter: spreads reconnects of many clients after a server restart.
    const ceiling = Math.min(
      RECONNECT_MAX_MS,
      RECONNECT_BASE_MS * Math.pow(2, this.attempts),
    )
    const delay = Math.floor(Math.random() * ceiling)
    this.attempts += 1
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.ensureConnecting()
    }, delay)
  }

  private resetWatchdog(): void {
    if (this.watchdogTimer) clearTimeout(this.watchdogTimer)
    this.watchdogTimer = setTimeout(() => {
      this.watchdogTimer = null
      // Silence past the heartbeat's answer window: treat as dead.
      this.clearTimers()
      this.dropSocket(WS_CLIENT_RECONNECT_CLOSE)
      this.scheduleReconnect()
    }, WATCHDOG_TIMEOUT_MS)
  }

  private sendRequest(
    kind: PendingRequest['kind'],
    ids: string[],
    reconnect: boolean,
  ): void {
    // Assigned ≤ cap already bounds every frame; chunk anyway so no frame can
    // ever exceed what the server reads.
    for (let i = 0; i < ids.length; i += this.deps.maxPerConnection) {
      const chunkIds = ids.slice(i, i + this.deps.maxPerConnection)
      this.pending.push({ kind, ids: chunkIds, reconnect })
      this.send({ type: kind, accountIds: chunkIds })
    }
  }

  private send(frame: Record<string, unknown>): void {
    if (!this.isOpen()) return
    try {
      this.socket?.send(JSON.stringify(frame))
    } catch {
      // A send on a closing socket; onclose will drive the reconnect.
    }
  }

  private dropSocket(code: number): void {
    const socket = this.socket
    this.socket = null
    this.pending = []
    this.serverSet = new Set()
    if (!socket) return
    socket.onopen = null
    socket.onmessage = null
    socket.onclose = null
    socket.onerror = null
    try {
      socket.close(code)
    } catch {
      // already closed
    }
  }

  private clearTimers(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer)
    if (this.watchdogTimer) clearTimeout(this.watchdogTimer)
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    if (this.stableTimer) clearTimeout(this.stableTimer)
    if (this.rotationTimer) clearTimeout(this.rotationTimer)
    this.rotationTimer = null
    this.abortRotation()
    this.clearDenialTimer()
    this.heartbeatTimer = null
    this.watchdogTimer = null
    this.reconnectTimer = null
    this.stableTimer = null
  }
}

function threadAccountIds(thread: unknown): string[] {
  if (!thread || typeof thread !== 'object') return []
  const t = thread as { accountId?: unknown; accountIds?: unknown }
  const ids = new Set<string>()
  if (t.accountId !== undefined && t.accountId !== null && t.accountId !== '') {
    ids.add(String(t.accountId))
  }
  if (Array.isArray(t.accountIds)) {
    for (const id of t.accountIds) {
      if (id !== undefined && id !== null && id !== '') ids.add(String(id))
    }
  }
  return [...ids]
}

/**
 * DLITE-5094: owns the thread-event connections. The caller hands it the
 * full set of accounts to watch; the pool spreads them over as many
 * connections as the server's per-connection cap requires and keeps each
 * connection's subscriptions, heartbeat and reconnects.
 */
export class ThreadEventSocketPool {
  private connections: ThreadEventConnection[] = []
  private stopped = false
  private wakeListening = false
  private readonly wakeHandler = () => this.connections.forEach(c => c.wake())

  constructor(private readonly deps: ThreadEventSocketPoolDeps) {}

  /** Replace the watched set. Only the difference goes over the wire. */
  setAccounts(ids: string[]): void {
    if (this.stopped) return
    const desired = new Set(ids.map(id => String(id)))

    for (const conn of this.connections) {
      const gone = [...conn.assigned].filter(id => !desired.has(id))
      if (gone.length > 0) conn.remove(gone)
    }

    const current = new Set<string>()
    this.connections.forEach(c => c.assigned.forEach(id => current.add(id)))
    const toAdd = [...desired].filter(id => !current.has(id))

    // Close emptied connections, except the last one while anything is watched.
    if (desired.size === 0) {
      this.closeAll()
      return
    }
    for (const conn of [...this.connections]) {
      if (conn.assigned.size === 0 && this.connections.length > 1) {
        conn.dispose()
        this.connections = this.connections.filter(c => c !== conn)
      }
    }

    // Fill existing spare capacity first, then open new connections.
    let rest = toAdd
    for (const conn of this.connections) {
      if (rest.length === 0) break
      const room = conn.capacity
      if (room <= 0) continue
      conn.add(rest.slice(0, room))
      rest = rest.slice(room)
    }
    while (rest.length > 0) {
      const conn = new ThreadEventConnection(this.deps, () =>
        this.listenForWake(),
      )
      this.connections.push(conn)
      conn.add(rest.slice(0, this.deps.maxPerConnection))
      rest = rest.slice(this.deps.maxPerConnection)
    }
  }

  /** Close every connection; nothing is scheduled afterwards. */
  stop(): void {
    this.stopped = true
    this.closeAll()
  }

  /** True only when the connection holding `accountId` is open and its last
   *  `subscribed` reply contains it: false before the first open, while
   *  reconnecting, after exhaustion or 1008, and for a denied id. */
  isLive(accountId: string): boolean {
    const id = String(accountId)
    const conn = this.connections.find(c => c.assigned.has(id))
    return !!conn && conn.isLive(id)
  }

  /** @internal for tests: accounts per connection, in connection order. */
  assignment(): string[][] {
    return this.connections.map(c => [...c.assigned])
  }

  private closeAll(): void {
    this.connections.forEach(c => c.dispose())
    this.connections = []
    this.unlistenForWake()
  }

  private wakeTarget() {
    if (this.deps.wakeTarget !== undefined) return this.deps.wakeTarget
    return typeof window !== 'undefined' ? window : null
  }

  private listenForWake(): void {
    const target = this.wakeTarget()
    if (this.wakeListening || !target) return
    this.wakeListening = true
    WAKE_EVENTS.forEach(e => target.addEventListener(e, this.wakeHandler))
  }

  private unlistenForWake(): void {
    const target = this.wakeTarget()
    if (!this.wakeListening || !target) return
    this.wakeListening = false
    WAKE_EVENTS.forEach(e => target.removeEventListener(e, this.wakeHandler))
  }
}
